// src/helpers/Collections.ts
import Requester from "./Requester";

type Options = { signal?: AbortSignal };

export type FAQItem = {
    question: string;
    answer: string;
};

export type CollectionSettings = {
    attributes: number[];            // attribute_value IDs used to filter
    similar_collections: number[];   // collection IDs for "You might also like"
    faq: FAQItem[];
};

export type CollectionDTO = {
    id: number;
    name: string;
    alias: string;
    short_description: string | null;
    settings: CollectionSettings;
    created_at?: string;
    updated_at?: string;
};

export type CreateCollectionPayload = {
    name: string;
    shortDescription?: string | null;
    attributes?: number[];
    similarCollections?: number[];
    faq?: FAQItem[];
};

export type UpdateCollectionPayload = CreateCollectionPayload;

export type Paginated<T> = {
    current_page: number;
    data: T[];
    from: number | null;
    last_page: number;
    per_page: number;
    to: number | null;
    total: number;
};

// List collections (auth required)
export function getCollections(
    limit: number = 10,
    page: number = 1,
    search: string | null = null,
    options: Options = {}
): Promise<Paginated<CollectionDTO>> {
    const requester = new Requester();

    return requester.get(
        requester.getUrl("api/collections", [
            { name: "limit", value: limit },
            { name: "page", value: page },
            { name: "search", value: search },
        ]),
        { signal: options.signal }
    );
}

// Get one collection by id (auth required)
export function getCollection(
    id: number,
    options: Options = {}
): Promise<CollectionDTO> {
    const requester = new Requester();
    return requester.get(requester.getUrl(`api/collections/${id}`), {
        signal: options.signal,
    });
}

// Create collection (auth required)
export function createCollection(
    payload: CreateCollectionPayload,
    options: Options = {}
): Promise<CollectionDTO> {
    const requester = new Requester();
    const body = {
        name: payload.name,
        shortDescription: payload.shortDescription ?? null,
        attributes: payload.attributes ?? [],
        similarCollections: payload.similarCollections ?? [],
        faq: payload.faq ?? [],
    };
    return requester.post(requester.getUrl("api/collections"), body, {
        signal: options.signal,
    });
}

// Update collection (auth required)
export function updateCollection(
    id: number,
    payload: UpdateCollectionPayload,
    options: Options = {}
): Promise<CollectionDTO> {
    const requester = new Requester();
    const body = {
        name: payload.name,
        shortDescription: payload.shortDescription ?? null,
        attributes: payload.attributes ?? [],
        similarCollections: payload.similarCollections ?? [],
        faq: payload.faq ?? [],
    };
    return requester.post(requester.getUrl(`api/collections/${id}`), body, {
        signal: options.signal,
    });
}

export default {
    getCollections,
    getCollection,
    createCollection,
    updateCollection,
};